import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WalletSections } from '../../../model';
import {
  downloadFinancialAdvisorProfile,
  retrieveHomeWalletSummary,
  retrieveLatestNews,
  retrieveOnboardingSelfPracticeData,
  getTasks,
  retrieveUnreadDocumentsCount,
  updateOnboardingCompileType,
  uploadSpecimen,
  startSigningProcess,
} from './home-thunks';
import { signatureArea } from '../profile/signature-area/signature-area-thunk';
import { HomeState, HomeWalletSectionMeta } from './home-types';
import {
  addHomeExtraReducers,
  dashboardEmptySummary,
  getMacroCategorySummaryPercentage,
  macroCategoryColorMap,
  sortMacroCategoriesByType,
} from './home-utils';
import { StatusCodeEnum } from '../../../api/generated';

const initialState: HomeState = {
  news: {
    requestStatus: 'idle',
  },
  documents: {
    requestStatus: 'idle',
  },
  signature: {
    requestStatus: 'idle',
  },
  financialAdvisor: {
    requestStatus: 'idle',
  },
  myWallet: {
    requestStatus: 'idle',
  },
  sharedAccounts: {
    requestStatus: 'idle',
  },
  toDoActivities: {
    requestStatus: 'idle',
  },
  selectedSection: 'MY_WALLET',
  practiceData: {
    requestStatus: 'idle',
  },
  updateOnboardingCompileTypeRequestStatus: 'idle',
  uploadSpecimenRequestStatus: 'idle',
  startSigningProcessRequestStatus: 'idle',
};

// Card filled by a wallet summary request: the tab that started it (meta),
// falling back to the current tab for actions without meta.
const homeWalletKey = (
  state: HomeState,
  meta?: Partial<HomeWalletSectionMeta>
): 'myWallet' | 'sharedAccounts' =>
  (meta?.section ?? state.selectedSection) === 'MY_WALLET'
    ? 'myWallet'
    : 'sharedAccounts';

const homeSlice = createSlice({
  name: 'features/home',
  initialState: initialState,
  reducers: {
    clearError(
      state,
      data: PayloadAction<
        keyof Omit<
          Omit<HomeState, 'selectedSection'>,
          | 'updateOnboardingCompileTypeRequestStatus'
          | 'uploadSpecimenRequestStatus'
          | 'startSigningProcessRequestStatus'
        >
      >
    ) {
      state[data.payload] = {
        requestStatus: 'idle',
      };
    },
    clearUpdateOnboardingCompileTypeRequestStatus(state) {
      state.updateOnboardingCompileTypeRequestStatus = 'idle';
    },
    clearUploadSpecimenRequestStatus(state) {
      state.uploadSpecimenRequestStatus = 'idle';
    },
    setSelectedWalletSection(state, action: PayloadAction<WalletSections>) {
      state.selectedSection = action.payload;
    },
    partialReset: (state) => ({
      ...initialState,
      toDoActivities: state.toDoActivities,
      practiceData: state.practiceData,
    }),
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    addHomeExtraReducers(builder, getTasks, 'toDoActivities');
    addHomeExtraReducers(builder, retrieveUnreadDocumentsCount, 'documents');
    builder.addCase(signatureArea.pending, (state) => {
      state.signature = {
        requestStatus: 'loading',
      };
    });
    builder.addCase(signatureArea.rejected, (state, action) => {
      state.signature = {
        requestStatus: 'failed',
        error: action.payload || 'GENERIC_ERROR',
      };
    });
    builder.addCase(signatureArea.fulfilled, (state, action) => {
      state.signature = {
        requestStatus: 'succeeded',
        value:
          action.payload?.filter(
            (doc) =>
              !doc.isExpired &&
              (doc.isMySignatureRequired ||
                doc.type?.practiceType === 'ONBOARDING_NUOVO_CLIENTE')
          )?.length || 0,
      };
    });
    addHomeExtraReducers(builder, retrieveLatestNews, 'news');
    addHomeExtraReducers(
      builder,
      downloadFinancialAdvisorProfile,
      'financialAdvisor'
    );
    addHomeExtraReducers(
      builder,
      retrieveOnboardingSelfPracticeData,
      'practiceData'
    );
    builder.addCase(retrieveHomeWalletSummary.pending, (state, action) => {
      state[homeWalletKey(state, action.meta)] = {
        requestStatus: 'loading',
      };
    });
    builder.addCase(retrieveHomeWalletSummary.rejected, (state, action) => {
      state[homeWalletKey(state, action.meta)] = {
        requestStatus: 'failed',
        error: action.payload || 'GENERIC_ERROR',
      };
    });
    builder.addCase(retrieveHomeWalletSummary.fulfilled, (state, action) => {
      state[homeWalletKey(state, action.meta)] = {
        requestStatus: 'succeeded',
        value:
          action?.payload.totalAmount.value === 0
            ? dashboardEmptySummary
            : {
                ...(action ?? {}).payload,
                macroCategories: sortMacroCategoriesByType([
                  ...(action?.payload.macroCategories ?? []),
                ])
                  .map((item) => ({
                    ...item,
                    color: macroCategoryColorMap[item.type],
                    percentage: getMacroCategorySummaryPercentage(
                      item.amount?.value,
                      action.payload.totalAmount?.value
                    ),
                  }))
                  .filter(
                    (item) =>
                      item.amount.value !== undefined &&
                      (item.type === 'TotalFia' ||
                        item.type === 'TotalPortfolio' ||
                        item.amount.value > 0)
                  ),
              },
      };
    });
    builder.addCase(updateOnboardingCompileType.pending, (state) => {
      state.updateOnboardingCompileTypeRequestStatus = 'loading';
    });
    builder.addCase(updateOnboardingCompileType.rejected, (state) => {
      state.updateOnboardingCompileTypeRequestStatus = 'failed';
    });
    builder.addCase(updateOnboardingCompileType.fulfilled, (state, action) => {
      state.updateOnboardingCompileTypeRequestStatus = 'succeded';
      if (state.practiceData?.value) {
        state.practiceData.value.compileType = action.payload;
      }
    });
    builder.addCase(uploadSpecimen.pending, (state) => {
      state.uploadSpecimenRequestStatus = 'loading';
    });
    builder.addCase(uploadSpecimen.rejected, (state) => {
      state.uploadSpecimenRequestStatus = 'failed';
    });
    builder.addCase(uploadSpecimen.fulfilled, (state, action) => {
      const isOk = action.payload?.status?.code === StatusCodeEnum.Ok;
      state.uploadSpecimenRequestStatus = isOk ? 'succeeded' : 'failed';
    });
    builder.addCase(startSigningProcess.pending, (state) => {
      state.startSigningProcessRequestStatus = 'loading';
    });
    builder.addCase(startSigningProcess.rejected, (state) => {
      state.startSigningProcessRequestStatus = 'failed';
    });
    builder.addCase(startSigningProcess.fulfilled, (state, action) => {
      const isOk = action.payload?.code === StatusCodeEnum.Ok;
      state.startSigningProcessRequestStatus = isOk ? 'succeeded' : 'failed';
    });
  },
});

export const homeActions = homeSlice.actions;

export default homeSlice.reducer;
